/** Shared header-pill rendering primitives, extracted from UnifiedAppHeader so
 *  a second toolbar host (the macOS-shell Takeoff toolbar strip) can render the
 *  exact same glass pills/menus without duplicating the JSX or importing the
 *  4700-line god-component. Behaviour-identical to the versions that used to
 *  live inline in UnifiedAppHeader.tsx — that file now re-exports these. */

import { useState, type ComponentType } from 'react';
import { motion } from 'motion/react';
import { ChevronDown } from '@/shared/ui/icons';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuArrow,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '../../../shared/ui/dropdown-menu';
import { Fragment } from 'react';
import { headerActionName } from './headerBadge';
import type { MobileHeaderAction, MobileHeaderSubAction } from './types';

/** A header value-picker rendered as a real native <select> styled as a glass
 *  pill (OS-drawn popup, no custom menu to maintain). Shared by the takeoff
 *  panel-section switcher and the BOQ bills/view switcher. */
export function HeaderPillSelect({
  value,
  ariaLabel,
  onChange,
  children,
  icon: Icon,
  variant = 'glass',
}: {
  value: string;
  ariaLabel: string;
  onChange: (value: string) => void;
  children: React.ReactNode;
  icon?: ComponentType<{ className?: string; strokeWidth?: number }>;
  variant?: 'glass' | 'toolbar';
}) {
  const toolbar = variant === 'toolbar';
  return (
    <div
      data-no-window-drag="true"
      className="group relative inline-flex items-center"
      style={{ pointerEvents: 'auto' }}
      onMouseDown={(event) => {
        event.stopPropagation();
      }}
    >
      {toolbar && (
        <>
          <span
            aria-hidden="true"
            data-slot="header-toolbar-select-fill"
            className="pointer-events-none absolute inset-x-1 top-1/2 h-9 -translate-y-1/2 rounded-full"
            style={{
              background: 'var(--header-filter-bg)',
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)',
              boxShadow: '0px 8px 24px 0px rgba(0,0,0,0.08), var(--glass-inset, none)',
            }}
          />
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-1 top-1/2 h-9 -translate-y-1/2 rounded-full bg-header-hover-bg opacity-0 transition-opacity duration-150 group-hover:opacity-100"
          />
        </>
      )}
      {toolbar && Icon && (
        <Icon
          aria-hidden
          className="pointer-events-none absolute left-3 z-[1] h-[18px] w-[18px] text-header-foreground transition-transform duration-150 group-hover:scale-110"
          strokeWidth={2.25}
        />
      )}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={ariaLabel}
        // A native select sizes itself to its WIDEST option, not the selected
        // one, so one long bill name ("Bill 1 - Groundworks and Substructure")
        // used to set the pill's width and crowd everything else off the mobile
        // header row. Cap it and let the closed state ellipsise; the OS-drawn
        // popup still shows every option in full.
        className={toolbar
          ? 'font-apple pointer-events-auto relative z-[1] h-11 max-w-[156px] cursor-pointer appearance-none rounded-full bg-transparent pl-9 pr-3 text-ellipsis text-[13px] font-semibold text-header-foreground focus:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-header-muted-foreground [&>option]:bg-popover [&>option]:text-popover-foreground sm:max-w-[220px] lg:max-w-none'
          : 'font-apple appearance-none h-9 cursor-pointer rounded-full pl-3.5 pr-8 text-[13px] font-semibold text-foreground bg-header-pill-fill focus:outline-none focus-visible:ring-2 focus-visible:ring-brand max-w-[132px] sm:max-w-[200px] lg:max-w-none text-ellipsis'}
        style={toolbar ? undefined : {
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          boxShadow: '0px 8px 24px 0px rgba(0,0,0,0.08)',
        }}
      >
        {children}
      </select>
      {!toolbar && (
        <ChevronDown
          aria-hidden
          className="pointer-events-none absolute right-2.5 h-4 w-4 text-muted-foreground"
          strokeWidth={2.25}
        />
      )}
    </div>
  );
}

/**
 * Wraps a header pill button in the shared DropdownMenu so clicking opens a
 * macOS-spec menu (vibrant fill, hairline separators, native focus + keyboard
 * nav) listing each sub-action — rather than a hand-rolled popover of plain
 * buttons. The trigger is a plain `<div asChild>`: Radix's Slot clones the
 * div cleanly, so the motion.button child keeps its ref/layout untouched and
 * the asChild-on-motion.button render failure never arises. Each pill owns its
 * own open state, which is why this is its own component, not inline.
 */
/**
 * Native-menu variant of HeaderPopoverPill: a transparent <select> laid over a
 * styled trigger, so the OS draws the list.
 *
 * What survives the move to a native control:
 *   - per-item on/off state, carried as a check in the option's own label
 *     (options cannot hold arbitrary markup, so the tick has to be text)
 *   - disabled items, natively
 *   - the divider, as an <optgroup> break
 * What does not: the leading icons. An <option> renders text only.
 *
 * The select always reads value="" and resets after each pick — these are
 * commands and toggles, not a value being chosen, so it must never display the
 * last-used item as though it were the current setting.
 */
export function HeaderNativeMenuPill({
  action,
  children,
}: {
  action: MobileHeaderAction;
  children: React.ReactNode;
}) {
  const subActions = action.subActions ?? [];
  const dividerAfter = action.subActionDividerAfter;
  const cut = dividerAfter ? subActions.findIndex((s) => s.id === dividerAfter) : -1;
  const groups = cut >= 0 && cut < subActions.length - 1
    ? [subActions.slice(0, cut + 1), subActions.slice(cut + 1)]
    : [subActions];

  const renderOption = (sub: MobileHeaderSubAction) => (
    <option key={sub.id} value={sub.id} disabled={!!sub.disabled}>
      {(sub.active ? '\u2713 ' : '') + sub.label}
    </option>
  );

  return (
    <div className="qs-native-menu relative flex items-center" data-no-window-drag="true">
      {children}
      <select
        aria-label={headerActionName(action.ariaLabel, action.badge)}
        value=""
        onChange={(e) => {
          const sub = subActions.find((s) => s.id === e.target.value);
          e.currentTarget.value = '';
          sub?.onClick();
        }}
        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
      >
        <option value="" disabled hidden>{action.ariaLabel ?? 'Menu'}</option>
        {groups.map((group, gi) =>
          groups.length > 1
            ? <optgroup key={gi} label="">{group.map(renderOption)}</optgroup>
            : group.map(renderOption),
        )}
      </select>
    </div>
  );
}

export function HeaderPopoverPill({
  action,
  children,
  side = 'bottom',
  align = 'end',
}: {
  action: MobileHeaderAction;
  children: React.ReactNode;
  /** Which edge of the trigger the menu opens from. Defaults to below, which
   *  is right for a horizontal toolbar; a vertical rail wants 'right' so the
   *  menu sits beside the trigger instead of on top of it. */
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
}) {
  const [open, setOpen] = useState(false);
  const subActions = action.subActions ?? [];
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <div data-no-window-drag="true" style={{ display: 'inline-flex' }}>{children}</div>
      </DropdownMenuTrigger>
      <DropdownMenuContent side={side} align={align} sideOffset={8} className="min-w-[240px]">
        {/* Caret back to the trigger — what makes this read as a callout
            anchored to its control rather than a slab floating near it. */}
        <DropdownMenuArrow className="fill-popover" />
        {subActions.map((sub) => {
          const SubIcon = sub.icon;
          const TrailingIcon = sub.trailingIcon;
          const showDivider = action.subActionDividerAfter === sub.id;
          return (
            <Fragment key={sub.id}>
              <DropdownMenuItem
                disabled={!!sub.disabled}
                onClick={() => sub.onClick()}
                onSelect={sub.keepsMenuPresented ? (e) => e.preventDefault() : undefined}
                aria-label={sub.ariaLabel ?? sub.label}
                // Two-line rows (label + sublabel) need to grow past the fixed
                // single-row height; the rest keep the standard 24 px row.
                className={sub.sublabel ? 'h-auto py-1.5' : undefined}
              >
                {SubIcon && <SubIcon strokeWidth={1.8} />}
                {sub.sublabel ? (
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate leading-tight">{sub.label}</span>
                    <span className="truncate text-[11px] font-normal leading-tight text-muted-foreground">
                      {sub.sublabel}
                    </span>
                  </span>
                ) : (
                  <span className="truncate">{sub.label}</span>
                )}
                {/* Trailing accessory (check / filter) marks the active row.
                    The text- class opts the icon out of the menu's default
                    muted-svg rule so it paints in the accent colour. */}
                {TrailingIcon && (
                  <TrailingIcon className="ml-auto text-primary" strokeWidth={2} />
                )}
              </DropdownMenuItem>
              {showDivider && <DropdownMenuSeparator />}
            </Fragment>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Plain glass pill button — icon-only or icon+label, active/disabled/badge
 *  states, matching Apple Liquid Glass spec (44px circle at rest, grows into
 *  a pill shape when it carries a label). Extracted from UnifiedAppHeader's
 *  renderMobileHeaderAction, minus the view-specific mobile-hide rules (those
 *  only apply inside the main app header, not a dedicated toolbar strip). If
 *  the action carries subActions it's wrapped in HeaderPopoverPill by the
 *  caller (see HeaderActionPill below). */
function PlainHeaderPill({ action }: { action: MobileHeaderAction }) {
  const Icon = action.icon;
  const isActive = !!action.active;
  const isDisabled = !!action.disabled;
  const hasSubActions = !!action.subActions && action.subActions.length > 0;
  return (
    <motion.button
      key={action.id}
      type="button"
      layout
      transition={{ type: 'spring', stiffness: 380, damping: 32, mass: 0.4 }}
      data-header-action-id={action.id}
      data-no-window-drag="true"
      aria-pressed={action.active === undefined ? undefined : isActive}
      aria-disabled={isDisabled || undefined}
      aria-haspopup={hasSubActions ? 'menu' : undefined}
      disabled={isDisabled}
      onMouseDown={(event) => {
        event.stopPropagation();
      }}
      onClick={(event) => {
        if (isDisabled) return;
        if (hasSubActions) return;
        action.onClick?.(event);
      }}
      className="group relative flex items-center justify-center transition-opacity disabled:opacity-40 disabled:active:opacity-40"
      style={{
        height: 44,
        minWidth: 44,
        width: action.label ? 'auto' : 44,
        padding: action.label ? '0 4px' : 0,
        borderRadius: 9999,
        pointerEvents: 'auto',
      }}
      aria-label={headerActionName(action.ariaLabel, action.badge)}
      whileHover={isDisabled ? undefined : { scale: 1.04 }}
      whileTap={isDisabled ? undefined : { scale: 0.94 }}
    >
      <span
        aria-hidden="true"
        className="pointer-events-none"
        style={{
          position: 'absolute',
          // 36 fill inside the 44 hit area, matching
          // renderMobileHeaderAction. See the note there: the button keeps its
          // full 44pt target while the visible circle lines up with the 36px
          // search field and select pills.
          left: 4,
          right: 4,
          height: 36,
          top: 'calc(50% - 18px)',
          zIndex: 0,
          borderRadius: 296,
          background: isActive ? 'var(--brand)' : 'var(--header-pill-fill)',
          mixBlendMode: isActive ? ('normal' as any) : ('var(--header-pill-blend)' as any),
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          boxShadow: isActive
            ? '0 10px 28px color-mix(in srgb, var(--brand) 32%, transparent), 0 2px 6px rgba(0,0,0,0.08)'
            : '0px 8px 24px 0px rgba(0,0,0,0.08), var(--glass-inset, none)',
          transition: 'background 280ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow 280ms cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      />
      <motion.span
        layout
        className="relative flex items-center justify-center"
        style={{
          minWidth: 36,
          height: 36,
          paddingLeft: action.label ? 10 : 0,
          paddingRight: action.label ? 10 : 0,
          gap: action.label && Icon ? 6 : 0,
          zIndex: 1,
        }}
      >
        {Icon && (
          <Icon
            style={{
              // 18 in a 36 fill, matching renderMobileHeaderAction.
              width: 18,
              height: 18,
              color: isActive ? 'white' : 'var(--foreground)',
              transition: 'color 280ms cubic-bezier(0.16, 1, 0.3, 1)',
            }}
            strokeWidth={2.25}
          />
        )}
        {action.label && (
          <span
            className="font-apple font-semibold whitespace-nowrap overflow-hidden"
            style={{
              color: isActive ? 'white' : 'var(--foreground)',
              // Solid chrome sets its own size through the variable; an inline literal would outrank it.
              fontSize: 'var(--header-pill-label-size, 13px)',
              lineHeight: '18px',
              letterSpacing: '-0.08px',
              transition: 'color 280ms cubic-bezier(0.16, 1, 0.3, 1)',
            }}
          >
            {action.label}
          </span>
        )}
      </motion.span>
      {typeof action.badge === 'number' && action.badge > 0 && (
        <span
          data-slot="header-action-badge"
          aria-hidden="true"
          className="pointer-events-none absolute top-1.5 right-1.5 size-2 rounded-full"
          style={action.badgeTone === 'brand'
            ? { background: 'var(--brand)', color: 'var(--brand-foreground)' }
            : { background: 'var(--destructive)', color: 'var(--destructive-foreground)' }}
        />
      )}
      {!action.label && action.ariaLabel && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute left-1/2 top-full z-50 mt-2 -translate-x-1/2 scale-95 whitespace-nowrap rounded-md bg-foreground px-2 py-1 text-xs font-medium text-background opacity-0 shadow-md transition-all duration-150 group-hover:scale-100 group-hover:opacity-100"
        >
          {action.ariaLabel}
          <span
            aria-hidden="true"
            className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-foreground"
          />
        </span>
      )}
    </motion.button>
  );
}

/** Public entry point — renders a single header action as a pill, wrapping it
 *  in HeaderPopoverPill when it carries subActions (the scale picker, display
 *  options, etc). Callers that need the native-<select> special case (e.g.
 *  `takeoff-panel`) should check `action.id` before calling this and render
 *  HeaderPillSelect directly instead — mirrors UnifiedAppHeader's own dispatch. */
export function HeaderActionPill({ action }: { action: MobileHeaderAction }) {
  const hasSubActions = !!action.subActions && action.subActions.length > 0;
  const pill = <PlainHeaderPill action={action} />;
  if (!hasSubActions) return pill;
  return <HeaderPopoverPill action={action}>{pill}</HeaderPopoverPill>;
}
